import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  PageHeader, SectionHeader, EmptyState, Tag, TYPE, cn, Button, Dialog, OptionRow,
} from './ui';
import { Expense, Income, PayoffPlan, CreditCard, Loan, ScheduleException } from '../types';
import { buildLedger, LedgerDay, LedgerEvent } from '../utils/calculations';
import { addDays, startOfDay, toDayKey, today as todayFn } from '../utils/finance/dates';
import { isRecurring } from '../utils/finance/moveOccurrence';
import { useAuth } from '../contexts/AuthContext';

/** What a drag is carrying. A plan payment is a stored row; everything else is
 *  one occurrence of a rule. */
type DragTarget =
  | { kind: 'payment'; paymentId: string }
  | { kind: 'expense' | 'income' | 'loan'; sourceId: string; recurring: boolean };

/** A move waiting on "just this one, or every one?". */
interface PendingMove {
  kind: 'expense' | 'income' | 'loan';
  sourceId: string;
  label: string;
  originalDate: Date;
  newDate: Date;
}

interface CalendarProps {
  currentBalance: number;
  balanceAsOf?: Date | null;
  expenses: Expense[];
  incomes: Income[];
  plans: PayoffPlan[];
  creditCards: CreditCard[];
  loans?: Loan[];
  lowBalanceThreshold?: number;
  /** Drop a plan payment on another day. Only plan rows can move: a minimum is
   *  derived from the card's due day and a recurring expense would need a
   *  "this one or all of them?" answer this gesture cannot ask. */
  onMovePayment?: (paymentId: string, date: Date) => Promise<void>;
  /** Put a payment marked as paid back to planned, so it can be moved again. */
  onUnconfirmPayment?: (paymentId: string) => Promise<void>;
  /** Occurrences already moved off the day their rule generates. */
  scheduleExceptions?: ScheduleException[];
  /** Move one occurrence of a recurring item, or a one-off, to another day.
   *  `scope` says whether the rule changes or just this occurrence. */
  onMoveOccurrence?: (input: {
    kind: 'expense' | 'income' | 'loan';
    sourceId: string;
    originalDate: Date;
    newDate: Date;
    scope: 'one' | 'all';
  }) => Promise<void>;
}

export const Calendar: React.FC<CalendarProps> = ({
  currentBalance,
  balanceAsOf,
  expenses,
  incomes,
  plans,
  creditCards,
  loans = [],
  lowBalanceThreshold = 0,
  onMovePayment,
  onUnconfirmPayment,
  scheduleExceptions = [],
  onMoveOccurrence,
}) => {
  const { user } = useAuth();
  // Only the viewed month is persisted now. The old `historicalBaseBalance` /
  // `lastCurrentBalance` keys existed to seed a hand-rolled BACKWARD replay
  // that drifted from the forward one; buildLedger replays backwards from the
  // real anchor, so the fabricated baseline (and its "Reset History" button)
  // are gone.
  const calendarDateKey = `user:${user?.id || 'anon'}:calendarCurrentDate`;

  const [currentDate, setCurrentDate] = useState(() => {
    const saved = localStorage.getItem(calendarDateKey);
    return saved ? new Date(saved) : new Date();
  });

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const dayWeekdayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const firstDayOfMonth = useMemo(
    () => new Date(currentDate.getFullYear(), currentDate.getMonth(), 1),
    [currentDate]
  );
  const lastDayOfMonth = useMemo(
    () => new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0),
    [currentDate]
  );

  // Grid always starts on a Sunday and shows whole weeks.
  const startDate = useMemo(
    () => addDays(firstDayOfMonth, -firstDayOfMonth.getDay()),
    [firstDayOfMonth]
  );
  const daysToShow = useMemo(() => {
    const span = Math.ceil(
      (lastDayOfMonth.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000)
    ) + 1;
    return Math.ceil(span / 7) * 7;
  }, [startDate, lastDayOfMonth]);

  /**
   * ONE ledger call. This replaces three separate projection paths: a forward
   * one, a hand-rolled backward replay seeded from localStorage, and a
   * transactions-only past branch that reported every balance as 0.
   */
  const days: LedgerDay[] = useMemo(
    () =>
      buildLedger({
        openingBalance: currentBalance,
        openingDate: balanceAsOf ? startOfDay(balanceAsOf) : todayFn(),
        from: startDate,
        to: addDays(startDate, daysToShow - 1),
        expenses,
        incomes,
        loans,
        creditCards,
        plans,
        lowBalanceThreshold,
        scheduleExceptions,
      }),
    [currentBalance, balanceAsOf, startDate, daysToShow, expenses, incomes, loans, creditCards, plans, lowBalanceThreshold, scheduleExceptions]
  );

  const calendarDays = useMemo(() => {
    const todayKey = toDayKey(new Date());
    const month = currentDate.getMonth();
    return days.map(day => ({
      day,
      date: day.date,
      isCurrentMonth: day.date.getMonth() === month,
      isToday: toDayKey(day.date) === todayKey,
      isSunday: day.date.getDay() === 0,
    }));
  }, [days, currentDate]);

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + (direction === 'next' ? 1 : -1), 1);
    setCurrentDate(newDate);
    localStorage.setItem(calendarDateKey, newDate.toISOString());
  };

  const getTransactionsByType = (events: LedgerEvent[]) => ({
    income: events.filter(e => e.type === 'income'),
    expenses: events.filter(e => e.type === 'expense'),
  });

  // Card identity, remaining balance and "is this the final payment" all come
  // from structured metadata now. They used to be recovered by stripping words
  // like ' Min Payment' out of the description, which broke on any card whose
  // name contained "Payment".
  const cardMeta = (e: LedgerEvent) =>
    e.source.kind === 'cardPayment' ? e.source : null;

  // Agenda (mobile): only days with activity, plus today.
  const todayCardRef = useRef<HTMLDivElement>(null);
  const agendaDays = useMemo(() => {
    const todayKey = toDayKey(new Date());
    return calendarDays.filter(
      d => d.isCurrentMonth && (d.day.events.length > 0 || toDayKey(d.date) === todayKey)
    );
  }, [calendarDays]);

  useEffect(() => {
    if (todayCardRef.current) {
      todayCardRef.current.scrollIntoView({ block: 'start', behavior: 'auto' });
    }
  }, [currentDate]);

  const usd = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;

  /**
   * Moving a payment is built on pointer events, not HTML5 drag and drop.
   *
   * Native DnD needs the browser to start a drag loop, the drop target to
   * preventDefault on dragover before any React state has settled, and a
   * dataTransfer to survive in between. It also does not exist on touch at
   * all. Pointer events have none of those conditions: the gesture is ours
   * from press to release, so it behaves the same in every browser, works on
   * a phone, and can be driven in a test.
   */
  const drag = useRef<{
    target: DragTarget;
    label: string;
    fromDayKey: string;
    startX: number;
    startY: number;
    moving: boolean;
  } | null>(null);

  /** Only what has to be painted: the floating label and the day under it. */
  const [ghost, setGhost] = useState<{ label: string; x: number; y: number } | null>(null);
  const [dropDayKey, setDropDayKey] = useState<string | null>(null);
  const [moving, setMoving] = useState(false);
  const [moveError, setMoveError] = useState<string | null>(null);

  /** The payment whose "already paid" mark the user is being offered to lift. */
  const [unconfirm, setUnconfirm] = useState<{ id: string; label: string; when: string } | null>(null);
  const [unconfirming, setUnconfirming] = useState(false);
  const [pendingMove, setPendingMove] = useState<PendingMove | null>(null);

  /**
   * What a pill would move, if anything.
   *
   * A plan row moves as a stored row. A bill, paycheck or loan payment is a
   * RULE, so moving one asks a question first — unless it happens only once,
   * where there is nothing to ask.
   *
   * A computed minimum never moves: its date IS the card's due day, and there
   * is no record behind it to change.
   */
  const movableTarget = (e: LedgerEvent): DragTarget | null => {
    const src = e.source;
    if (src.kind === 'cardPayment') {
      if (!onMovePayment) return null;
      return src.paymentId && src.paymentKind !== 'MINIMUM' && src.status !== 'CONFIRMED'
        ? { kind: 'payment', paymentId: src.paymentId }
        : null;
    }
    if (!onMoveOccurrence) return null;
    if (src.kind === 'expense') {
      const item = expenses.find(x => x.id === src.expenseId);
      if (!item) return null;
      return { kind: 'expense', sourceId: item.id, recurring: isRecurring(item.frequency) };
    }
    if (src.kind === 'income') {
      const item = incomes.find(x => x.id === src.incomeId);
      if (!item) return null;
      return { kind: 'income', sourceId: item.id, recurring: isRecurring(item.frequency) };
    }
    if (src.kind === 'loan') {
      const item = (loans ?? []).find(x => x.id === src.loanId);
      if (!item) return null;
      return { kind: 'loan', sourceId: item.id, recurring: true };
    }
    return null;
  };

  /** The day sitting under the pointer, grid cell or agenda row alike. */
  const dayKeyAt = (x: number, y: number): string | null => {
    const el = document.elementFromPoint(x, y) as HTMLElement | null;
    return el?.closest<HTMLElement>('[data-day]')?.dataset.day ?? null;
  };

  const endDrag = () => {
    drag.current = null;
    setGhost(null);
    setDropDayKey(null);
  };

  const fromDayKeyToDate = (dayKey: string) => {
    const [y, m, d] = dayKey.split('-').map(Number);
    return new Date(y, m - 1, d);
  };

  const runOccurrenceMove = async (
    move: PendingMove,
    scope: 'one' | 'all'
  ) => {
    if (!onMoveOccurrence) return;
    setMoving(true);
    setMoveError(null);
    try {
      await onMoveOccurrence({
        kind: move.kind,
        sourceId: move.sourceId,
        originalDate: move.originalDate,
        newDate: move.newDate,
        scope,
      });
    } catch (err: any) {
      setMoveError(err?.message || `Could not move ${move.label}.`);
    } finally {
      setMoving(false);
    }
  };

  const commitMove = async (
    target: DragTarget,
    label: string,
    fromDayKey: string,
    toDayKeyStr: string
  ) => {
    const newDate = fromDayKeyToDate(toDayKeyStr);

    if (target.kind === 'payment') {
      if (!onMovePayment) return;
      setMoving(true);
      setMoveError(null);
      try {
        await onMovePayment(target.paymentId, newDate);
      } catch (err: any) {
        setMoveError(err?.message || `Could not move ${label}.`);
      } finally {
        setMoving(false);
      }
      return;
    }

    const move: PendingMove = {
      kind: target.kind,
      sourceId: target.sourceId,
      label,
      originalDate: fromDayKeyToDate(fromDayKey),
      newDate,
    };
    // A one-off has a single occurrence, so "this one" and "all of them" are
    // the same thing — asking would be a question with one answer.
    if (!target.recurring) {
      void runOccurrenceMove(move, 'all');
      return;
    }
    setPendingMove(move);
  };

  /** The gesture lives on the window, so it keeps working when the pointer
   *  leaves the pill — which it does immediately. */
  useEffect(() => {
    if (!onMovePayment && !onMoveOccurrence) return;

    const onMove = (e: PointerEvent) => {
      const g = drag.current;
      if (!g) return;
      if (!g.moving) {
        // A few pixels of slop, so a click is never mistaken for a drag.
        if (Math.hypot(e.clientX - g.startX, e.clientY - g.startY) < 5) return;
        g.moving = true;
      }
      e.preventDefault();
      setGhost({ label: g.label, x: e.clientX, y: e.clientY });
      setDropDayKey(dayKeyAt(e.clientX, e.clientY));
    };

    const onUp = (e: PointerEvent) => {
      const g = drag.current;
      endDrag();
      if (!g || !g.moving) return;
      const target = dayKeyAt(e.clientX, e.clientY);
      if (!target || target === g.fromDayKey) return;
      void commitMove(g.target, g.label, g.fromDayKey, target);
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') endDrag();
    };

    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', endDrag);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', endDrag);
      window.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onMovePayment, onMoveOccurrence]);

  const cardName = (id: string) => creditCards.find(c => c.id === id)?.name ?? null;

  /** One event pill. A card payment is marked by weight, not hue: the final
   *  payment fills ink, a plan payment is outlined ink, a minimum is plain. */
  const eventPill = (transaction: any, key: string, dense: boolean, index = 0, dayKey = '') => {
    const meta = cardMeta(transaction);
    const isPlanPayment = !!meta && meta.paymentKind !== 'MINIMUM';
    const isFinal = !!meta && meta.isFinal;
    const remaining = meta ? meta.remainingAfter : null;
    /** Marked as paid. Its date is history, so it cannot be dragged — which is
     *  worth SAYING, because otherwise the pill just quietly refuses to move. */
    const paidId =
      meta && meta.paymentId && meta.paymentKind !== 'MINIMUM' && meta.status === 'CONFIRMED'
        ? meta.paymentId
        : null;
    const isIncome = transaction.type === 'income';
    const title =
      `${isFinal ? 'Final payment — ' : isPlanPayment ? 'Plan payment — ' : ''}` +
      `${transaction.label}: ${isIncome ? '+' : '\u2212'}${usd(transaction.amount)}` +
      `${remaining !== null ? ` | Remaining ${usd(remaining)}` : ''}`;

    // In a tile this narrow the pill's own weight already says which kind of
    // payment this is, so "Chase Slate Payment" spends its width restating the
    // outline. Show just the card, and keep the full label in the tooltip —
    // otherwise two Chase cards both truncate to "CHASE…".
    const label = (meta && cardName(meta.cardId)) || transaction.label;

    const labelEl = (
      <span
        className={cn(
          // Wraps at spaces rather than truncating. Normal tracking at 12px
          // fits far more than 9px monospace did at 0.06em.
          'text-[11.5px] leading-[1.32] min-w-0 break-words flex-1',
          isFinal || isPlanPayment
            ? 'font-semibold text-accent'
            : isIncome
              ? 'font-medium text-money-in'
              : 'font-medium text-ink-soft',
        )}
      >
        {isFinal && '\u2713 '}
        {label}
        {paidId && (
          // A rule under the label, not a colour: "paid" is a state of the
          // record, not a kind of money, so it must not borrow a money hue.
          <span
            className={cn(TYPE.metaTight, 'ml-1.5 text-ink-muted border-b border-rule-strong')}
          >
            paid
          </span>
        )}
      </span>
    );

    const amountEl = (
      <span
        className={cn(
          // Monospace so amounts line up down the column.
          'text-[11.5px] font-semibold whitespace-nowrap shrink-0',
          isIncome ? 'text-money-in' : isFinal || isPlanPayment ? 'text-accent' : 'text-ink'
        )}
      >
        {isIncome ? '+' : '\u2212'}{usd(transaction.amount)}
      </span>
    );

    const moveTarget = movableTarget(transaction);
    const moveId = moveTarget ? 'yes' : null;

    return (
      <div
        key={key}
        title={
          moveId
            ? `${title}\nDrag to another day to move it`
            : paidId && onUnconfirmPayment
              ? `${title}\nMarked as paid — click to undo that and move it again`
              : title
        }
        onClick={() => {
          if (!paidId || !onUnconfirmPayment) return;
          setUnconfirm({
            id: paidId,
            label,
            when: transaction.date.toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
            }),
          });
        }}
        onPointerDown={e => {
          // Left button (or a touch/pen contact) only.
          if (!moveId || (e.pointerType === 'mouse' && e.button !== 0)) return;
          drag.current = {
            target: moveTarget!,
            label,
            fromDayKey: dayKey,
            startX: e.clientX,
            startY: e.clientY,
            moving: false,
          };
        }}
        className={cn(
          'flex flex-wrap items-baseline gap-x-2 py-1',
          // touch-none so a drag on a phone moves the payment instead of
          // scrolling the month; select-none so it does not smear a text
          // selection across the cell on the way.
          moveId && 'cursor-grab active:cursor-grabbing touch-none select-none',
          paidId && onUnconfirmPayment && 'cursor-pointer',
          drag.current?.label === label && ghost && 'opacity-40',
          // Rows inside a cell divide on the lighter rule; the cell's own
          // edges are the grid. One weight per level, never mixed.
          index > 0 && 'border-t border-rule',
          // A final payment fills the row rather than adding another marker.
          isFinal && 'bg-accent-tint rounded-xs -mx-[7px] px-[7px] mt-0.5'
        )}
      >
        {labelEl}
        <span className="ml-auto">{amountEl}</span>
      </div>
    );
  };

  /** End-of-day balance chip. Red only when the day is negative or breaches
   *  the user's threshold — the two states that actually need attention. */
  const balanceChip = (day: any, dense: boolean) => {
    return (
      <div
        className={cn(
          // The strip closes the cell on the grid line and reads as a footer
          // rather than one more transaction. State is carried by rule weight
          // as much as by fill, so it survives in greyscale: hairline when
          // normal, an ochre rule when low, a doubled crimson rule and an
          // inverted fill when the day is actually negative.
          'flex items-baseline justify-between gap-2',
          day.day.isNegative
            ? 'border-t-[3px] border-double border-bad bg-bad'
            : day.day.belowThreshold
              ? 'border-t border-warn'
              : 'border-t border-rule bg-ground-sunk',
          dense
            ? 'mt-2 py-1.5'
            : 'mt-auto -mx-2.5 px-2.5 py-[7px]'
        )}
        title={
          day.day.belowThreshold
            ? `Below your low-balance threshold: ${usd(day.day.balance)}`
            : `Balance: ${usd(day.day.balance)}`
        }
      >
        {/* Only the two states that need saying. A figure on the closing rule
            of a day is a closing balance — "EOD" spent a slot on every cell to
            repeat what the position already says.

            The glyph stays where it earns its place: it is the non-colour half
            of the cue, readable at 10px and in greyscale where the fill alone
            would not be. */}
        {(day.day.isNegative || day.day.belowThreshold) && (
          <span
            className={cn(
              TYPE.tag,
              day.day.isNegative ? 'text-bad-tint' : 'text-warn'
            )}
          >
            {day.day.isNegative ? '! NEG' : '▲ LOW'}
          </span>
        )}
        <span
          className={cn(
            // ml-auto, not justify-between: on a normal day the balance is the
            // only child, and justify-between alone would park it on the left.
            'ml-auto text-[13px] font-semibold tabular-nums',
            day.day.isNegative
              ? 'text-bad-tint'
              : day.day.belowThreshold
                ? 'text-warn border-b border-warn'
                : 'text-ink'
          )}
        >
          {usd(day.day.balance)}
        </span>
      </div>
    );
  };

  return (
    <>
      <PageHeader
        heading="Financial Calendar"
        subhead="Every scheduled movement, day by day, with the balance it leaves behind."
      />

      {/*
        The month control follows the page down.
        A month's grid is taller than the viewport, and the agenda far taller
        still, so leaving this in the page header meant scrolling back to the
        top to change month — which is to say, not using it. It sticks under
        the app header, which is fixed, so its offset is the measured header
        height rather than zero.
      */}
      <div
        className="sticky z-30 bg-ground border-b border-rule mt-8 py-3 flex items-center justify-end gap-4"
        style={{ top: 'var(--app-header-h)' }}
      >
        {/* One face, one size. This is a control, not row metadata: mono
            carries the meaning "metadata" in this system, and TYPE.meta is
            11px — the smallest type in the app — which is the wrong weight for
            the only thing in this bar. TYPE.chip is what navTab and
            buttonLabel use, which is what this is. */}
        <button
          onClick={() => navigateMonth('prev')}
          aria-label="Previous month"
          className={cn(TYPE.chip, 'text-ink-soft hover:text-accent transition-colors duration-[120ms] px-2')}
        >
          {'\u2190'}
        </button>
        <span className={cn(TYPE.chip, 'text-ink min-w-[140px] text-center')}>
          {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
        </span>
        <button
          onClick={() => navigateMonth('next')}
          aria-label="Next month"
          className={cn(TYPE.chip, 'text-ink-soft hover:text-accent transition-colors duration-[120ms] px-2')}
        >
          {'\u2192'}
        </button>
      </div>

      {/* Agenda — the only view under md. Rows, not cards. */}
      <section className="md:hidden mt-7">
        <SectionHeader label="Agenda" note={`${agendaDays.length} days`} />
        {agendaDays.length === 0 ? (
          <EmptyState>No transactions this month</EmptyState>
        ) : (
          // The agenda is the same system as the grid — a card on the canvas,
          // not a rule-bounded block.
          <div className="bg-surface border border-rule-base rounded-xl overflow-hidden mt-5">
            {agendaDays.map((day, index) => {
              const { income, expenses } = getTransactionsByType(day.day.events);
              return (
                <div
                  key={index}
                  ref={day.isToday ? todayCardRef : undefined}
                  data-day={toDayKey(day.date)}
                  className={cn(
                    'border-b border-rule last:border-b-0 py-4',
                    day.isToday ? 'border-l-[3px] border-l-ink-soft pl-[13px] pr-4' : 'px-4',
                    ghost && dropDayKey === toDayKey(day.date) && 'bg-accent-tint'
                  )}
                >
                  <div className="flex items-baseline justify-between gap-3 mb-2">
                    <span className={cn(TYPE.meta, 'text-ink')}>
                      {dayWeekdayNames[day.date.getDay()]}, {monthNames[day.date.getMonth()]} {day.date.getDate()}
                    </span>
                    {day.isToday && <Tag weight="solid">Today</Tag>}
                  </div>
                  <div>
                    {income.map((t, i) => eventPill(t, `i-${i}`, false, i, toDayKey(day.date)))}
                    {expenses.map((t, i) => eventPill(t, `e-${i}`, false, income.length + i, toDayKey(day.date)))}
                  </div>
                  {balanceChip(day, true)}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Month grid — md and up. */}
      <section className="hidden md:block mt-7">
        {/* Cells are cards on the tinted canvas, separated by a gap. A day
            boundary and a week boundary are both just the gap, so there is no
            rule whose weight can be wrong. */}
        {/* A drag is only obvious while it is happening; say what is and is
            not movable, and report the outcome. */}
        {onMovePayment && (ghost || moving || moveError) && (
          <div
            className={cn(
              // Fixed, never in flow. In flow it appeared the instant a drag
              // began and pushed the grid down by its own height, so the cell
              // under the cursor was no longer the cell under the cursor — a
              // payment dropped on the 13th landed on the 6th, one row up.
              // pointer-events-none for the same reason: it must never be
              // what the hit test finds.
              'fixed left-1/2 -translate-x-1/2 z-40 pointer-events-none',
              'top-[calc(var(--app-header-h)+12px)]',
              'px-4 py-2.5 border-l-2 shadow-lift',
              moveError ? 'border-bad bg-bad-tint' : 'border-accent bg-accent-tint'
            )}
            role="status"
          >
            <p className={cn(TYPE.small, moveError ? 'text-ink' : 'text-ink-soft')}>
              {moveError
                ? moveError
                : moving
                  ? 'Moving…'
                  : `Drop ${ghost?.label} on a day to move that payment. Later payments keep their own dates.`}
            </p>
          </div>
        )}

        <div className="border border-rule-base bg-rule-base flex flex-col gap-px">
          <div className="grid grid-cols-7 gap-px">
            {dayNames.map(day => (
              <div key={day} className={cn(TYPE.sectionLabel, 'bg-ground-sunk text-ink-muted px-2.5 py-2')}>
                {day}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-px">
          {calendarDays.map((day, index) => {
            const { income, expenses } = getTransactionsByType(day.day.events);
            return (
              <div
                key={index}
                data-day={toDayKey(day.date)}
                className={cn(
                  'min-h-cell px-2.5 pt-2.5 flex flex-col',
                  // Cells are square and butt together; the 1px gap over the
                  // rule-coloured backing IS the gridline, so day and week
                  // boundaries are the same weight by construction.
                  !day.isCurrentMonth
                    ? 'bg-ground'
                    : day.day.isNegative
                      ? 'bg-bad-cell'
                      : day.day.belowThreshold
                        ? 'bg-warn-cell'
                        : 'bg-surface',
                  // Today carries no cell treatment — the day number alone
                  // says it, and one marker is enough.
                  // The day under a dragged payment. Accent, because this is
                  // the one moment the cell is something you are acting on.
                  ghost && dropDayKey === toDayKey(day.date) && 'shadow-focus-accent bg-accent-tint'
                )}
              >
                {/* An adjacent month is dimmed by fading the CONTENT, not the
                    cell. Fading the cell made it translucent over the grid's
                    ink background, so those days rendered as dark blocks. */}
                <div
                  className={cn(
                    'flex flex-col flex-1',
                    !day.isCurrentMonth && 'opacity-45'
                  )}
                >
                  <div className="mb-2">
                    <span
                      className={cn(
                        // The size sits inside each branch, never beside them:
                        // two font-size utilities on one element resolve by
                        // stylesheet order, not by the order written.
                        'font-semibold',
                        day.isToday
                          // Inverse ink, one step light: emphasis that claims
                          // nothing. The accent is reserved for things you can
                          // act on, and it is already spent in this grid on
                          // the cell under a dragged payment.
                          ? 'text-[12px] bg-ink-soft text-surface rounded-md px-[7px] py-[3px]'
                          : day.isCurrentMonth
                            ? 'text-[13px] text-ink'
                            : 'text-[13px] text-ink-muted'
                      )}
                    >
                      {day.isToday ? day.date.getDate() : String(day.date.getDate()).padStart(2, '0')}
                    </span>
                  </div>
                  <div className="overflow-hidden flex-grow">
                    {income.map((t, i) => eventPill(t, `i-${i}`, true, i, toDayKey(day.date)))}
                    {expenses.map((t, i) => eventPill(t, `e-${i}`, true, income.length + i, toDayKey(day.date)))}
                  </div>
                  {day.isCurrentMonth && balanceChip(day, false)}
                </div>
              </div>
            );
          })}
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-x-7 gap-y-2">
          <span className="text-[11px] text-ink-muted">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-money-in align-middle mr-2" />
            Green — money in
          </span>
          <span className="text-[11px] text-ink-muted">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-accent align-middle mr-2" />
            Blue — payment plan
          </span>
          <span className="text-[11px] text-ink-muted">
            <span className="inline-block w-3 h-3 rounded bg-accent-tint border border-accent align-middle mr-2" />
            Filled — final payment
          </span>
          <span className="text-[11px] text-ink-muted">
            <span className="inline-block w-3 h-3 rounded bg-warn-tint border border-warn align-middle mr-2" />
            Amber — balance below your threshold
          </span>
        </div>
      </section>

      {pendingMove && (
        <Dialog
          title={`Move ${pendingMove.label} to ${pendingMove.newDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}?`}
          subhead={`${pendingMove.label} repeats, so there is more than one of it. Which did you mean?`}
          footer={
            <div className="flex justify-end">
              <Button variant="ghost" disabled={moving} onClick={() => setPendingMove(null)}>
                Cancel
              </Button>
            </div>
          }
        >
          <div className="flex flex-col gap-3">
            <OptionRow
              title="Just this one"
              detail={`Only the ${pendingMove.originalDate.toLocaleDateString('en-US', { month: 'long' })} one moves. Every other month stays where it is.`}
              busy={moving}
              onClick={async () => {
                const move = pendingMove;
                setPendingMove(null);
                await runOccurrenceMove(move, 'one');
              }}
            />
            <OptionRow
              title="Every one"
              detail={`${pendingMove.label} falls on this day from here on — and in the months already shown, since the schedule itself changes.`}
              busy={moving}
              onClick={async () => {
                const move = pendingMove;
                setPendingMove(null);
                await runOccurrenceMove(move, 'all');
              }}
            />
          </div>
        </Dialog>
      )}

      {unconfirm && (
        <Dialog
          title="Mark this payment as not paid?"
          subhead={
            <>
              {unconfirm.label} on {unconfirm.when} is recorded as paid, so its date
              is treated as history and it cannot be moved. Marking it not paid puts
              it back in the plan — it does not change the amount or the schedule.
            </>
          }
          footer={
            <div className="flex flex-wrap justify-end gap-3">
              <Button variant="ghost" disabled={unconfirming} onClick={() => setUnconfirm(null)}>
                Leave it as paid
              </Button>
              <Button
                variant="primary"
                disabled={unconfirming}
                onClick={async () => {
                  if (!onUnconfirmPayment) return;
                  setUnconfirming(true);
                  setMoveError(null);
                  try {
                    await onUnconfirmPayment(unconfirm.id);
                    setUnconfirm(null);
                  } catch (err: any) {
                    setMoveError(err?.message || `Could not update ${unconfirm.label}.`);
                    setUnconfirm(null);
                  } finally {
                    setUnconfirming(false);
                  }
                }}
              >
                {unconfirming ? 'Working…' : 'Mark as not paid'}
              </Button>
            </div>
          }
        >
          <p className={cn(TYPE.small, 'text-ink-soft')}>
            Use this when the prompt on sign-in was answered by mistake, or when a
            payment you had recorded has not actually gone out yet.
          </p>
        </Dialog>
      )}

      {/* The payment itself, under the cursor. Native drag drew this for us;
          owning the gesture means drawing it ourselves — which is also why it
          can be styled to match the app rather than a browser screenshot. */}
      {ghost && (
        <div
          data-testid="drag-ghost"
          aria-hidden="true"
          className="fixed z-50 pointer-events-none px-2.5 py-1.5 bg-surface border border-accent shadow-lift"
          style={{ left: ghost.x + 12, top: ghost.y + 12 }}
        >
          <span className="text-[11.5px] font-semibold text-accent whitespace-nowrap">
            {ghost.label}
          </span>
        </div>
      )}
    </>
  );
};
